/**
 * The command catalog — what the app can do, and nothing about which keys do
 * it. Bindings live in `keymap.ts`; behaviour is supplied by whichever
 * component owns it via `useCommandHandler`.
 *
 * Keeping this file key-free is what makes the shortcuts panel possible: it
 * renders the catalog, and reads chords out of the resolved keymap beside it.
 * A command whose chord the user has changed still has the same id, title and
 * group here, so nothing downstream has to care.
 *
 * Adding a command is three edits, and TypeScript enforces two of them:
 *   1. add the id to `CommandId` in `types.ts`,
 *   2. add the entry here (a missing entry fails `commandsById`'s totality
 *      check in `catalog.test.ts`),
 *   3. add a default binding in `DEFAULT_KEYMAP` (a missing one is a compile
 *      error, because that map is a total `Record<CommandId, …>`).
 */
import type {
  Command,
  CommandGroup,
  CommandId,
  CommandScope,
  TranslationKey,
} from './types';

/**
 * Ordered — the shortcuts panel renders groups in this order, and within a
 * group, commands in catalog order. Ordering also breaks conflict ties (see
 * `resolveBindings`), so it is data, not cosmetics.
 */
export const COMMANDS: readonly Command[] = [
  // -------------------------------------------------------------------------
  // General — global scope
  // -------------------------------------------------------------------------
  {
    id: 'general.showShortcuts',
    titleKey: 'commands.general.showShortcuts.title',
    descriptionKey: 'commands.general.showShortcuts.description',
    group: 'general',
  },
  {
    id: 'general.toggleAssistant',
    titleKey: 'commands.general.toggleAssistant.title',
    descriptionKey: 'commands.general.toggleAssistant.description',
    group: 'general',
  },
  {
    id: 'general.closeOverlay',
    titleKey: 'commands.general.closeOverlay.title',
    descriptionKey: 'commands.general.closeOverlay.description',
    group: 'general',
    // Escape has to reach a dialog whose focus is sitting in a text field, so
    // it overrules the shape-based inference that would ignore inputs. It also
    // must NOT preventDefault: Escape cancels an IME composition and reverts a
    // native input, and swallowing that breaks typing in ja/zh-TW.
    options: { ignoreInputs: false, preventDefault: false },
  },

  // -------------------------------------------------------------------------
  // Navigation — sequences, global scope
  // -------------------------------------------------------------------------
  {
    id: 'navigation.goHome',
    titleKey: 'commands.navigation.goHome.title',
    group: 'navigation',
  },
  {
    id: 'navigation.goMeshgen',
    titleKey: 'commands.navigation.goMeshgen.title',
    group: 'navigation',
  },
  {
    id: 'navigation.goSimops',
    titleKey: 'commands.navigation.goSimops.title',
    group: 'navigation',
  },

  // -------------------------------------------------------------------------
  // Viewer — only while a mesh viewer is mounted
  // -------------------------------------------------------------------------
  {
    id: 'viewer.resetCamera',
    titleKey: 'commands.viewer.resetCamera.title',
    descriptionKey: 'commands.viewer.resetCamera.description',
    group: 'viewer',
    scope: 'viewer',
  },

  // -------------------------------------------------------------------------
  // Editing / list surfaces
  // -------------------------------------------------------------------------
  {
    id: 'designPoints.selectNext',
    titleKey: 'commands.designPoints.selectNext.title',
    group: 'editing',
    scope: 'designPoints',
    // Arrow keys repeat while held; a row step per repeat is the wanted
    // behaviour, so `requireReset` stays off. `preventDefault` is the
    // handler's call, not TanStack's: it must first see whether a menu or a
    // select trigger already took the key (`defaultPrevented`), which an
    // up-front preventDefault would make always true (ENG-3003).
    options: { preventDefault: false },
  },
  {
    id: 'designPoints.selectPrevious',
    titleKey: 'commands.designPoints.selectPrevious.title',
    group: 'editing',
    scope: 'designPoints',
    options: { preventDefault: false },
  },
  {
    id: 'designPoints.closeDetail',
    titleKey: 'commands.designPoints.closeDetail.title',
    group: 'editing',
    scope: 'designPoints',
    // `ignoreInputs: true` is NOT redundant here. TanStack's shape-based
    // default returns FALSE for Escape (it assumes Escape should always
    // reach a dialog), which is right for `general.closeOverlay` and wrong
    // here: Escape with the caret in the assistant composer or the write-up
    // editor means "get out of this field", and closing the design-point
    // detail out from under the user is not that. The `window` listener this
    // replaced had an explicit `isTypingTarget` guard for exactly this.
    options: { preventDefault: false, ignoreInputs: true },
  },
  {
    id: 'reportWriteup.save',
    titleKey: 'commands.reportWriteup.save.title',
    descriptionKey: 'commands.reportWriteup.save.description',
    group: 'editing',
    scope: 'reportWriteup',
    // A save shortcut is useless if it stops working the moment the caret is
    // in the editor, which is the only place it is ever pressed.
    options: { ignoreInputs: false },
  },
];

/** Lookup by id. Built once; the catalog is static. */
export const commandsById: ReadonlyMap<CommandId, Command> = new Map(
  COMMANDS.map((command) => [command.id, command]),
);

/** A command's scope, with the `'global'` default applied. */
export function scopeOf(command: Command): CommandScope {
  return command.scope ?? 'global';
}

/** Group render order for the shortcuts panel. */
export const COMMAND_GROUP_ORDER = [
  'general',
  'navigation',
  'viewer',
  'editing',
] as const;

/** i18n keys for the group headings. */
export const COMMAND_GROUP_LABEL_KEYS = {
  general: 'commands.groups.general',
  navigation: 'commands.groups.navigation',
  viewer: 'commands.groups.viewer',
  editing: 'commands.groups.editing',
} as const satisfies Record<CommandGroup, TranslationKey>;

/** i18n keys naming a scope, for the "only active while…" hint. */
export const SCOPE_LABEL_KEYS = {
  global: 'commands.scopes.global',
  viewer: 'commands.scopes.viewer',
  designPoints: 'commands.scopes.designPoints',
  reportWriteup: 'commands.scopes.reportWriteup',
} as const satisfies Record<CommandScope, TranslationKey>;
